import { create as createQrCode } from 'qrcode';

/**
 * Turns an arbitrary string (in practice the `otpauth://` URI Cognito hands us
 * during TOTP enrolment) into the geometry needed to draw a QR code as inline
 * SVG.
 *
 * Why not use `qrcode`'s own renderers?
 *
 *  - `toCanvas` / `toDataURL` need a real 2D canvas context. jsdom does not
 *    implement one, so every test touching the enrolment screen would break,
 *    and any browser that blocks canvas would silently render nothing.
 *  - `toString` returns a *string* of SVG markup, which would have to be
 *    injected with `dangerouslySetInnerHTML`, and its shape differs between the
 *    package's Node entry point (`lib/index.js`) and its browser entry point
 *    (`lib/browser.js`, selected by webpack via the `browser` field). That
 *    divergence means the thing under test would not be the thing shipped.
 *
 * `QRCode.create` is the one export that is byte-identical across both entry
 * points and is pure computation, so we call that and draw the modules
 * ourselves as ordinary React elements.
 */

/**
 * The QR spec requires a light border (the "quiet zone") of at least four
 * modules on every side. Scanners use it to find the symbol edges; without it
 * many phones simply never lock on.
 */
export const QR_QUIET_ZONE_MODULES = 4;

export interface QrCodeSvg {
  /** Modules per side of the symbol itself, excluding the quiet zone. */
  moduleCount: number;
  /** Side length of the square viewBox: `moduleCount + 2 * QR_QUIET_ZONE_MODULES`. */
  viewBoxSize: number;
  /**
   * An SVG path `d` covering every dark module, expressed in viewBox units
   * (1 unit = 1 module) and already offset by the quiet zone. Consecutive dark
   * modules in a row are merged into a single horizontal run to keep the path
   * short.
   */
  path: string;
}

/**
 * Builds the inline-SVG geometry for `text`. Throws if `text` cannot be encoded
 * (for example if it is empty, or too long for the largest QR version).
 */
export function buildQrCodeSvg(text: string): QrCodeSvg {
  // 'M' (~15% recovery) is what authenticator-app enrolment codes conventionally
  // use: enough resilience for a phone camera, small enough to stay crisp.
  const { modules } = createQrCode(text, { errorCorrectionLevel: 'M' });

  const moduleCount = modules.size;
  const data = modules.data;
  const quiet = QR_QUIET_ZONE_MODULES;
  const runs: string[] = [];

  for (let row = 0; row < moduleCount; row++) {
    let runStart = -1;

    // Deliberately runs one column past the edge so a run that reaches the
    // right-hand side is still flushed.
    for (let col = 0; col <= moduleCount; col++) {
      const isDark = col < moduleCount && data[row * moduleCount + col] === 1;

      if (isDark && runStart === -1) {
        runStart = col;
      } else if (!isDark && runStart !== -1) {
        const length = col - runStart;
        runs.push(`M${runStart + quiet} ${row + quiet}h${length}v1h-${length}z`);
        runStart = -1;
      }
    }
  }

  return {
    moduleCount,
    viewBoxSize: moduleCount + quiet * 2,
    path: runs.join(''),
  };
}
